import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardTitle, Row, Col, FormGroup, Label } from 'reactstrap';
import { Button } from 'react-bootstrap';
import { AvForm, AvField } from "availity-reactstrap-validation";
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { upload } from '../../helper/api_helper';
import * as Utils from "../../Utils";
import Header from '../../layouts/Header';
import Footer from '../../layouts/Footer';
import Loader from '../../layouts/Loader';
import { getCategoryList } from '../../helper/category_helper';
import {
    MAX_PRODUCT_IMAGES,
    buildDefaultImageSlotKeys,
    emptyImageSlotMeta,
    emptyImageSlotRecord,
    parseProductImagesBySlot,
    appendProductImagesToFormData,
    slotNumberFromKey,
    getProductById,
    imageSlotKey,
} from '../../helper/product_helper';

const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

const initialImageMeta = (slotKeys = buildDefaultImageSlotKeys()) =>
    slotKeys.reduce((acc, key) => {
        acc[key] = emptyImageSlotMeta();
        return acc;
    }, {});

const initialExistingUrls = (slotKeys = buildDefaultImageSlotKeys()) =>
    emptyImageSlotRecord(slotKeys, null);

function AddProduct({ currentData, onSuccess, onCancel }) {
    const user = useSelector(state => state.user);
    const [loading, setLoading] = useState(false);
    const [loadingProductImages, setLoadingProductImages] = useState(false);

    const defaultSlotKeys = buildDefaultImageSlotKeys();
    const [imageSlotKeys, setImageSlotKeys] = useState(defaultSlotKeys);
    const [images, setImages] = useState(() => emptyImageSlotRecord(defaultSlotKeys, null));
    const [imagePreviews, setImagePreviews] = useState(() => emptyImageSlotRecord(defaultSlotKeys, null));
    const [selectedSizes, setSelectedSizes] = useState([]);
    const [isBestseller, setIsBestseller] = useState(false);
    const [ingredients, setIngredients] = useState('');
    const [benefits, setBenefits] = useState('');
    const [imageMeta, setImageMeta] = useState(() => initialImageMeta(defaultSlotKeys));
    const [existingImageUrls, setExistingImageUrls] = useState(() => initialExistingUrls(defaultSlotKeys));

    const [categories, setCategories] = useState([]);
    const availableSizes = ['10ml', '20ml', '30ml'];

    const getCategoryValue = () => {
        if (!currentData?.category) return '';
        if (typeof currentData.category === 'object') {
            return currentData.category._id || '';
        }
        return String(currentData.category);
    };

    const applyProductImageState = (product) => {
        const { meta, existingUrls, slotKeys } = parseProductImagesBySlot(product);
        setImageSlotKeys(slotKeys);
        setImageMeta(meta);
        setExistingImageUrls(existingUrls);

        const previews = emptyImageSlotRecord(slotKeys, null);
        slotKeys.forEach((key) => {
            if (existingUrls[key]) {
                previews[key] = Utils.getImageUrl(existingUrls[key]);
            }
        });
        setImagePreviews(previews);
        setImages(emptyImageSlotRecord(slotKeys, null));
    };

    const hydrateFormFromProduct = (product) => {
        if (!product) return;
        setSelectedSizes(product?.sizes || []);
        setIsBestseller(product?.bestSeller || product?.bestseller || false);
        setIngredients(product?.ingredients ? product.ingredients.join(', ') : '');
        setBenefits(product?.benefits ? product.benefits.join(', ') : '');
        applyProductImageState(product);
    };

    useEffect(() => {
        fetchCategories();

        if (!currentData?._id) {
            return;
        }

        hydrateFormFromProduct(currentData);

        let cancelled = false;
        setLoadingProductImages(true);

        getProductById(currentData._id)
            .then((res) => {
                if (cancelled) return;
                const product =
                    res?.data?.product ||
                    res?.product ||
                    res?.data ||
                    null;
                if (product) {
                    hydrateFormFromProduct(product);
                }
            })
            .catch((error) => {
                console.error('Failed to load product images for edit', error);
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingProductImages(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [currentData?._id]);

    const fetchCategories = async () => {
        try {
            const token = user?.token || localStorage.getItem('adminToken');
            // Try updating to the same endpoint used in index.js for consistency
            const res = await getCategoryList(token);
            if (res) {
                // Check if data is array or wrapped in data property
                const cats = res.data?.categories || res.data || (Array.isArray(res) ? res : []);
                setCategories(cats);
            }
        } catch (error) {
            console.error('Failed to fetch categories', error);
        }
    };

    const resetForm = () => {
        const slots = buildDefaultImageSlotKeys();
        setImageSlotKeys(slots);
        setImages(emptyImageSlotRecord(slots, null));
        setImagePreviews(emptyImageSlotRecord(slots, null));
        setSelectedSizes([]);
        setIsBestseller(false);
        setIngredients('');
        setBenefits('');
        setImageMeta(initialImageMeta(slots));
        setExistingImageUrls(initialExistingUrls(slots));
    }

    const getImagePreviewSrc = (imgKey) => {
        if (imagePreviews[imgKey]) {
            return imagePreviews[imgKey];
        }
        if (existingImageUrls[imgKey]) {
            return Utils.getImageUrl(existingImageUrls[imgKey]);
        }
        return null;
    };

    const slotHasImage = (imgKey) =>
        Boolean(images[imgKey] || existingImageUrls[imgKey] || imagePreviews[imgKey]);

    const ensureNextEmptySlotKey = (keys, imagesState, existingState) => {
        const emptyKey = keys.find((key) => !imagesState[key] && !existingState[key]);
        if (emptyKey) {
            return { keys, newKey: emptyKey };
        }
        const maxSlot = keys.reduce(
            (max, key) => Math.max(max, slotNumberFromKey(key) || 0),
            0
        );
        if (maxSlot >= MAX_PRODUCT_IMAGES) {
            return { keys, newKey: null };
        }
        const newKey = imageSlotKey(maxSlot + 1);
        return { keys: [...keys, newKey], newKey };
    };

    const handleMultipleImagesChange = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        if (!files.length) return;

        let keys = [...imageSlotKeys];
        let nextImages = { ...images };
        let nextPreviews = { ...imagePreviews };
        let nextMeta = { ...imageMeta };
        let nextExisting = { ...existingImageUrls };
        let skipped = 0;

        for (const file of files) {
            const { keys: updatedKeys, newKey } = ensureNextEmptySlotKey(
                keys,
                nextImages,
                nextExisting
            );
            if (!newKey) {
                skipped += 1;
                continue;
            }
            keys = updatedKeys;
            if (!nextMeta[newKey]) {
                nextMeta[newKey] = emptyImageSlotMeta();
            }
            if (nextExisting[newKey] === undefined) {
                nextExisting[newKey] = null;
            }
            nextImages[newKey] = file;
            try {
                nextPreviews[newKey] = await readFileAsDataUrl(file);
            } catch {
                nextPreviews[newKey] = null;
            }
        }

        setImageSlotKeys(keys);
        setImages(nextImages);
        setImagePreviews(nextPreviews);
        setImageMeta(nextMeta);
        setExistingImageUrls(nextExisting);

        if (skipped > 0) {
            toast.warning(
                `${skipped} file(s) skipped — maximum ${MAX_PRODUCT_IMAGES} images per product.`
            );
        } else {
            toast.success(`${files.length - skipped} image(s) added. Fill SEO details below if needed.`);
        }
    };

    const handleAddImageSlot = () => {
        const maxSlot = imageSlotKeys.reduce(
            (max, key) => Math.max(max, slotNumberFromKey(key) || 0),
            0
        );
        if (maxSlot >= MAX_PRODUCT_IMAGES) {
            toast.error(`You can add up to ${MAX_PRODUCT_IMAGES} images per product.`);
            return;
        }
        const newKey = `image${maxSlot + 1}`;
        setImageSlotKeys((prev) => [...prev, newKey]);
        setImages((prev) => ({ ...prev, [newKey]: null }));
        setImagePreviews((prev) => ({ ...prev, [newKey]: null }));
        setImageMeta((prev) => ({ ...prev, [newKey]: emptyImageSlotMeta() }));
        setExistingImageUrls((prev) => ({ ...prev, [newKey]: null }));
    };

    const handleImageMetaChange = (imageKey, field, value) => {
        setImageMeta((prev) => ({
            ...prev,
            [imageKey]: {
                ...prev[imageKey],
                [field]: value,
            },
        }));
    };

    const handleImageChange = (e, imageKey) => {
        const file = e.target.files[0];
        if (file) {
            setImages(prev => ({ ...prev, [imageKey]: file }));

            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreviews(prev => ({ ...prev, [imageKey]: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    }

    const handleRemoveImage = (imageKey) => {
        setImages(prev => ({ ...prev, [imageKey]: null }));
        setImagePreviews(prev => ({ ...prev, [imageKey]: null }));
        setExistingImageUrls(prev => ({ ...prev, [imageKey]: null }));
    }

    const handleSizeToggle = (size) => {
        if (selectedSizes.includes(size)) {
            setSelectedSizes(selectedSizes.filter(s => s !== size));
        } else {
            setSelectedSizes([...selectedSizes, size]);
        }
    }

    const handleSubmit = (event, values) => {
        if (selectedSizes.length === 0) {
            toast.error("Please select at least one size");
            return;
        }

        if (!loading) {
            setLoading(true);

            const formData = new FormData();

            // Add ID for update
            if (currentData?._id) {
                formData.append('id', currentData._id);
            }

            formData.append('name', values.name);
            formData.append('description', values.description);
            formData.append('price', values.price);

            // Add discountPrice if provided (number inputs may return number, not string)
            const discountPriceStr = values.discountPrice != null && values.discountPrice !== ''
                ? String(values.discountPrice).trim()
                : '';
            if (discountPriceStr !== '') {
                const discountPrice = Number(discountPriceStr);
                const price = Number(values.price);
                if (discountPrice > price) {
                    toast.error("Discount price cannot be higher than actual price.");
                    setLoading(false);
                    return;
                }
                formData.append('discountPrice', discountPriceStr);
            }

            formData.append('category', values.category);
            formData.append('subCategory', values.subCategory);
            formData.append('sizes', JSON.stringify(selectedSizes));
            formData.append('bestseller', isBestseller);
            formData.append('stock', values.stock || 0);
            formData.append('howToUse', values.howToUse || '');
            formData.append('hsnCode', (values.hsnCode || '33049990').toString().trim() || '33049990');
            formData.append(
                'gstRate',
                values.gstRate != null && values.gstRate !== ''
                    ? String(values.gstRate)
                    : '18'
            );

            // Handle ingredients array
            if (ingredients && ingredients.trim()) {
                const ingredientsArray = ingredients.split(',').map(item => item.trim()).filter(item => item);
                formData.append('ingredients', JSON.stringify(ingredientsArray));
            }

            // Handle benefits array
            if (benefits && benefits.trim()) {
                const benefitsArray = benefits.split(',').map(item => item.trim()).filter(item => item);
                formData.append('benefits', JSON.stringify(benefitsArray));
            }

            appendProductImagesToFormData(formData, {
                imageFiles: images,
                imageMeta,
                existingImageUrls,
                isUpdate: Boolean(currentData?._id),
                slotKeys: imageSlotKeys,
            });

            const token = user?.token || localStorage.getItem('adminToken');

            // Fix: Use correct upload endpoint and explicitly append id for update
            const endpoint = currentData?._id ? "product/update" : "product/add";
            if (currentData?._id) {
                // Ensure id is exactly matching what backend expects e.g. productId if id didn't work
                formData.append('productId', currentData._id);
            }

            upload(endpoint, formData, token)
                .then(res => {
                    setLoading(false);
                    if (res?.success) {
                        toast.success(currentData?._id ? "Product updated successfully!" : "Product added successfully!");
                        resetForm();
                        if (onSuccess) onSuccess();
                    } else {
                        toast.error(res?.message || `Failed to ${currentData?._id ? 'update' : 'add'} product`);
                    }
                })
                .catch(err => {
                    setLoading(false);
                    toast.error("Something went wrong!");
                    console.error(err);
                });
        }
    }

    return (
        <>
            {loading && <Loader />}
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="d-md-flex align-items-center justify-content-between mb-4">
                    <div>
                        <ol className="breadcrumb fs-sm mb-1">
                            <li className="breadcrumb-item">
                                <a href="#" onClick={(e) => { e.preventDefault(); onCancel(); }}>Products</a>
                            </li>
                            <li className="breadcrumb-item active" aria-current="page">
                                {currentData ? 'Edit Product' : 'Add New Product'}
                            </li>
                        </ol>
                        <h4 className="main-title mb-0">
                            {currentData ? 'Edit Product' : 'Add New Product'}
                        </h4>
                    </div>
                    <Button
                        onClick={onCancel}
                        variant="outline-secondary"
                    >
                        <i className="ri-arrow-left-line me-2"></i>Back to Products
                    </Button>
                </div>

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardBody>
                                <CardTitle className="h5 mb-4">Product Information</CardTitle>
                                <AvForm onValidSubmit={handleSubmit}>
                                    <Row>
                                        <Col md={12}>
                                            <AvField
                                                name="name"
                                                label="Product Name"
                                                placeholder="Enter product name"
                                                type="text"
                                                value={currentData?.name || ''}
                                                errorMessage="Product name is required"
                                                validate={{ required: { value: true } }}
                                            />
                                        </Col>

                                        <Col md={12}>
                                            <AvField
                                                name="description"
                                                label="Description"
                                                placeholder="Enter product description"
                                                type="textarea"
                                                rows="3"
                                                value={currentData?.description || ''}
                                                errorMessage="Description is required"
                                                validate={{ required: { value: true } }}
                                            />
                                        </Col>

                                        <Col md={6}>
                                            <AvField
                                                name="price"
                                                label="Price"
                                                placeholder="Enter price"
                                                type="number"
                                                value={currentData?.price || ''}
                                                errorMessage="Price is required"
                                                validate={{
                                                    required: { value: true },
                                                    min: { value: 0 }
                                                }}
                                            />
                                        </Col>

                                        <Col md={6}>
                                            <AvField
                                                name="discountPrice"
                                                label="Discount Price"
                                                placeholder="Enter discount price (optional)"
                                                type="number"
                                                value={currentData?.discountPrice || ''}
                                                validate={{
                                                    min: { value: 0 }
                                                }}
                                            />
                                        </Col>

                                        <Col md={6}>
                                            <AvField
                                                name="stock"
                                                label="Stock Quantity"
                                                placeholder="Enter stock quantity"
                                                type="number"
                                                value={currentData?.stock || 0}
                                                validate={{
                                                    min: { value: 0 }
                                                }}
                                            />
                                        </Col>

                                        <Col md={6}>
                                            <AvField
                                                name="hsnCode"
                                                label="HSN Code"
                                                placeholder="e.g. 33049990"
                                                type="text"
                                                value={currentData?.hsnCode || '33049990'}
                                                helpMessage="Used on GST tax invoices (cosmetics default 33049990)"
                                            />
                                        </Col>

                                        <Col md={6}>
                                            <AvField
                                                name="gstRate"
                                                label="GST Rate (%)"
                                                placeholder="18"
                                                type="number"
                                                value={
                                                    currentData?.gstRate != null
                                                        ? currentData.gstRate
                                                        : 18
                                                }
                                                validate={{
                                                    min: { value: 0 },
                                                    max: { value: 100 }
                                                }}
                                                helpMessage="Default 18% for beauty products"
                                            />
                                        </Col>

                                        <Col md={6}>
                                            <AvField
                                                name="category"
                                                label="Category"
                                                type="select"
                                                value={getCategoryValue()}
                                                errorMessage="Category is required"
                                                validate={{ required: { value: true } }}
                                            >
                                                <option value="">Select Category</option>
                                                {categories.map(cat => (
                                                    <option key={cat._id || Math.random()} value={cat._id}>
                                                        {cat.title || cat.name || 'Unnamed Category'}
                                                    </option>
                                                ))}
                                            </AvField>
                                        </Col>

                                        {/* <Col md={6}>
                                            <AvField
                                                name="subCategory"
                                                label="Sub Category"
                                                type="select"
                                                value={currentData?.subCategory || ''}
                                                errorMessage="Sub category is required"
                                                validate={{ required: { value: true } }}
                                            >
                                                <option value="">Select Sub Category</option>
                                                {subCategories.map(subCat => (
                                                    <option key={subCat} value={subCat}>{subCat}</option>
                                                ))}
                                            </AvField>
                                        </Col> */}

                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Bestseller</Label>
                                                <div className="form-check form-switch">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        checked={isBestseller}
                                                        onChange={(e) => setIsBestseller(e.target.checked)}
                                                    />
                                                    <label className="form-check-label">
                                                        Mark as Bestseller
                                                    </label>
                                                </div>
                                            </FormGroup>
                                        </Col>

                                        <Col md={12}>
                                            <FormGroup>
                                                <Label>Sizes *</Label>
                                                <div className="d-flex flex-wrap gap-2">
                                                    {availableSizes.map(size => (
                                                        <Button
                                                            key={size}
                                                            type="button"
                                                            variant={selectedSizes.includes(size) ? 'primary' : 'outline-primary'}
                                                            size="sm"
                                                            onClick={() => handleSizeToggle(size)}
                                                        >
                                                            {size}
                                                        </Button>
                                                    ))}
                                                </div>
                                                {selectedSizes.length > 0 && (
                                                    <small className="text-muted mt-2 d-block">
                                                        Selected: {selectedSizes.join(', ')}
                                                    </small>
                                                )}
                                            </FormGroup>
                                        </Col>

                                        {/* <Col md={12}>
                                            <FormGroup>
                                                <Label>Ingredients (comma separated)</Label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="e.g., Ingredient 1, Ingredient 2, Ingredient 3"
                                                    value={ingredients}
                                                    onChange={(e) => setIngredients(e.target.value)}
                                                />
                                                <small className="text-muted">Separate multiple ingredients with commas</small>
                                            </FormGroup>
                                        </Col> */}

                                        {/* <Col md={12}>
                                            <FormGroup>
                                                <Label>Benefits (comma separated)</Label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="e.g., Benefit 1, Benefit 2, Benefit 3"
                                                    value={benefits}
                                                    onChange={(e) => setBenefits(e.target.value)}
                                                />
                                                <small className="text-muted">Separate multiple benefits with commas</small>
                                            </FormGroup>
                                        </Col> */}

                                        <Col md={12}>
                                            <AvField
                                                name="howToUse"
                                                label="How To Use"
                                                placeholder="Enter usage instructions"
                                                type="textarea"
                                                rows="3"
                                                value={currentData?.howToUse || ''}
                                            />
                                        </Col>

                                        {/* Image Upload Section */}
                                        <Col md={12}>
                                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                                                <Label className="mb-0 fw-semibold">Product Images</Label>
                                                <span className="text-muted small">
                                                    {imageSlotKeys.length} / {MAX_PRODUCT_IMAGES} slots
                                                </span>
                                            </div>
                                            <div
                                                className="border rounded p-3 mb-3"
                                                style={{ backgroundColor: '#f8f9fa' }}
                                            >
                                                <Label className="fw-semibold mb-2">
                                                    Upload multiple images at once
                                                </Label>
                                                <input
                                                    type="file"
                                                    className="form-control"
                                                    accept="image/*"
                                                    multiple
                                                    onChange={handleMultipleImagesChange}
                                                    disabled={imageSlotKeys.length >= MAX_PRODUCT_IMAGES}
                                                />
                                                <small className="text-muted d-block mt-2">
                                                    Select many files together (Ctrl/Cmd+click). Each image gets its own SEO fields below.
                                                </small>
                                                <div className="d-flex flex-wrap gap-2 mt-3">
                                                    <Button
                                                        type="button"
                                                        variant="primary"
                                                        size="sm"
                                                        onClick={handleAddImageSlot}
                                                        disabled={imageSlotKeys.length >= MAX_PRODUCT_IMAGES}
                                                    >
                                                        + Add one more slot
                                                    </Button>
                                                </div>
                                            </div>
                                            {loadingProductImages && (
                                                <p className="text-muted small mb-3">Loading product images…</p>
                                            )}
                                            <Row>
                                                {imageSlotKeys.map((imgKey) => {
                                                    const slotNum = slotNumberFromKey(imgKey) || 0;
                                                    const previewSrc = getImagePreviewSrc(imgKey);
                                                    return (
                                                    <Col md={6} key={imgKey} className="mb-3">
                                                        <FormGroup>
                                                            <Label>Image {slotNum}</Label>
                                                            <input
                                                                type="file"
                                                                className="form-control"
                                                                accept="image/*"
                                                                onChange={(e) => handleImageChange(e, imgKey)}
                                                            />
                                                            {previewSrc && (
                                                                <div className="mt-2 position-relative">
                                                                    <img
                                                                        src={previewSrc}
                                                                        alt={imageMeta[imgKey]?.altText || `Preview ${slotNum}`}
                                                                        style={{
                                                                            width: '100%',
                                                                            maxHeight: '200px',
                                                                            objectFit: 'cover',
                                                                            borderRadius: '8px',
                                                                            border: '2px solid #ddd'
                                                                        }}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-dark btn-sm position-absolute"
                                                                        onClick={() => handleRemoveImage(imgKey)}
                                                                        style={{
                                                                            top: '5px',
                                                                            right: '5px',
                                                                            borderRadius: '50%',
                                                                            width: '32px',
                                                                            height: '32px',
                                                                            padding: '0',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            fontSize: '18px',
                                                                            fontWeight: 'bold',
                                                                            boxShadow: '0 2px 5px rgba(0,0,0,0.3)'
                                                                        }}
                                                                        title="Remove Image"
                                                                    >
                                                                        ×
                                                                    </button>
                                                                </div>
                                                            )}
                                                            {existingImageUrls[imgKey] && !images[imgKey] && (
                                                                <small className="text-muted d-block mt-2">
                                                                    Saved on server — choose a new file above to replace.
                                                                </small>
                                                            )}
                                                            {!slotHasImage(imgKey) && (
                                                                <small className="text-muted d-block mt-2">
                                                                    Empty slot — use multi-upload above or pick a file here.
                                                                </small>
                                                            )}
                                                            <div className="mt-3">
                                                                <Label className="small text-muted mb-1">Alt Text</Label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm mb-2"
                                                                    placeholder="Describe the image for accessibility and SEO"
                                                                    value={imageMeta[imgKey]?.altText || ''}
                                                                    onChange={(e) => handleImageMetaChange(imgKey, 'altText', e.target.value)}
                                                                />
                                                                <Label className="small text-muted mb-1">Image Title</Label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm mb-2"
                                                                    placeholder="Optional img title attribute"
                                                                    value={imageMeta[imgKey]?.title || ''}
                                                                    onChange={(e) => handleImageMetaChange(imgKey, 'title', e.target.value)}
                                                                />
                                                                <Label className="small text-muted mb-1">Image Caption</Label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm mb-2"
                                                                    placeholder="Short caption for storefront figcaption"
                                                                    value={imageMeta[imgKey]?.caption || ''}
                                                                    onChange={(e) => handleImageMetaChange(imgKey, 'caption', e.target.value)}
                                                                />
                                                                <Label className="small text-muted mb-1">Image Description</Label>
                                                                <textarea
                                                                    className="form-control form-control-sm mb-2"
                                                                    rows="2"
                                                                    placeholder="Longer SEO notes (optional on PDP)"
                                                                    value={imageMeta[imgKey]?.description || ''}
                                                                    onChange={(e) => handleImageMetaChange(imgKey, 'description', e.target.value)}
                                                                />
                                                                <Label className="small text-muted mb-1">SEO Filename / Slug</Label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    placeholder="e.g. careveli-vitamin-c-serum-hero"
                                                                    value={imageMeta[imgKey]?.seoFilename || ''}
                                                                    onChange={(e) => handleImageMetaChange(imgKey, 'seoFilename', e.target.value)}
                                                                />
                                                            </div>
                                                        </FormGroup>
                                                    </Col>
                                                    );
                                                })}
                                            </Row>
                                        </Col>
                                    </Row>

                                    <div className="d-flex justify-content-end gap-2 mt-4">
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            onClick={onCancel}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            style={{ backgroundColor: Utils.themeColor, border: 'none' }}
                                            disabled={loading}
                                        >
                                            {loading ? 'Processing...' : (currentData ? 'Update Product' : 'Add Product')}
                                        </Button>
                                    </div>
                                </AvForm>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <Footer />
            </div>
        </>
    );
}

export default AddProduct;
